import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Bot, AlertTriangle } from "lucide-react";

const CONSENT_KEY = "rheumhub_ai_consent_v1";

interface AiConsentModalProps {
  variant: "nurse" | "patient" | "doctor" | "pharmacist" | "physio" | "ot";
  onConsented: () => void;
}

export function AiConsentModal({ variant, onConsented }: AiConsentModalProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consented = localStorage.getItem(CONSENT_KEY);
    if (!consented) setShow(true);
    else onConsented();
  }, []);

  if (!show) return null;

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "1");
    setShow(false);
    onConsented();
  };

  const iconBg = variant === "nurse" ? "bg-teal-50" : variant === "doctor" ? "bg-indigo-50" : variant === "pharmacist" ? "bg-violet-50" : variant === "physio" ? "bg-emerald-50" : variant === "ot" ? "bg-amber-50" : "bg-rose-50";
  const iconColour = variant === "nurse" ? "text-teal-600" : variant === "doctor" ? "text-indigo-600" : variant === "pharmacist" ? "text-violet-700" : variant === "physio" ? "text-emerald-600" : variant === "ot" ? "text-amber-600" : "text-rose-500";
  const title = variant === "nurse" ? "Assistant" : variant === "doctor" ? "Clinical Assistant" : variant === "pharmacist" ? "Medicines Adviser" : variant === "physio" ? "Rehabilitation Assistant" : variant === "ot" ? "OT Clinical Assistant" : "Guide";
  const btnClass = variant === "nurse"
    ? "bg-teal-700 hover:bg-teal-800"
    : variant === "doctor"
    ? "bg-indigo-700 hover:bg-indigo-800"
    : variant === "pharmacist"
    ? "bg-violet-700 hover:bg-violet-800"
    : variant === "physio"
    ? "bg-emerald-700 hover:bg-emerald-800"
    : variant === "ot"
    ? "bg-amber-700 hover:bg-amber-800"
    : "bg-rose-500 hover:bg-rose-600";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 space-y-6 max-h-[calc(100vh-2rem)] overflow-y-auto my-auto">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${iconBg}`}>
            <Bot className={`h-6 w-6 ${iconColour}`} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Before you use the AI {title}</h2>
            <p className="text-sm text-slate-500">Please read and agree to continue</p>
          </div>
        </div>

        {variant === "nurse" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <p><strong>Clinical decision support, not a replacement.</strong> This AI is grounded in NMC Code, NICE, BSR, and EULAR guidelines to support your practice — it does not replace clinical judgment or professional accountability.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <p><strong>Your NMC accountability remains.</strong> All clinical decisions are your professional responsibility. Never apply AI guidance to individual patients without appropriate clinical review.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <p><strong>Conversations are stored and audited</strong> under our UK GDPR data processing policy. You may delete your data at any time in Settings.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <p><strong>Usage limit:</strong> 30 messages per hour to ensure fair access for all users.</p>
            </div>
          </div>
        )}

        {variant === "doctor" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
              <p><strong>Clinical decision support, not a substitute for judgment.</strong> This AI is grounded in GMC Good Medical Practice, NICE, BSR, and EULAR guidance. It does not replace your clinical assessment, professional accountability, or GMC registration obligations.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
              <p><strong>Your GMC accountability is unaffected.</strong> All clinical decisions remain your professional responsibility. This tool does not constitute a second opinion or specialist advice — verify responses against primary sources before applying to individual patient care.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
              <p><strong>Do not enter identifiable patient data.</strong> Conversations are stored and audited under our UK GDPR data processing policy. Never input patient names, NHS numbers, or other direct identifiers. Delete your data at any time in Settings.</p>
            </div>
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>AI responses may contain errors.</strong> Large language models can hallucinate or cite outdated evidence. Always cross-reference with current NICE/BSR guidelines before acting on any AI-generated information.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
              <p><strong>Usage limit:</strong> 30 messages per hour to ensure fair access for all users.</p>
            </div>
          </div>
        )}

        {variant === "pharmacist" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
              <p><strong>Medicines optimisation decision support only.</strong> This AI adviser is grounded in GPhC Standards for Pharmacy Professionals, RPS guidance, BNF/BNFC, drug SPCs, and NHSE medicines optimisation frameworks. It does not replace clinical judgment, the BNF, or the SPC.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
              <p><strong>Your GPhC accountability is unaffected.</strong> All prescribing and clinical decisions remain your professional responsibility. This tool does not constitute a second opinion. Always verify responses against the current BNF, SPC, and local formulary before applying to clinical practice.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
              <p><strong>Do not enter identifiable patient data.</strong> Conversations are stored and audited under our UK GDPR data processing policy for pharmacovigilance governance purposes. Delete your data at any time in Settings.</p>
            </div>
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>AI responses may contain errors.</strong> Always cross-reference with the current BNF, MHRA guidance, and NICE TAs before acting on AI-generated information. Report suspected ADRs via the MHRA Yellow Card scheme regardless of any AI suggestions.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
              <p><strong>Usage limit:</strong> 30 messages per hour to ensure fair access for all users.</p>
            </div>
          </div>
        )}

        {variant === "physio" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p><strong>Rehabilitation decision support only.</strong> This AI assistant is grounded in CSP Standards of Practice, NICE MSK guidelines, ACPR rheumatology physiotherapy guidance, and EULAR physical activity recommendations. It does not replace clinical assessment or your professional judgment.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p><strong>Your HCPC accountability is unaffected.</strong> All clinical decisions remain your professional responsibility. Exercise prescription must be based on individual patient assessment. Do not apply general guidance directly to specific patients without clinical reasoning.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p><strong>Do not enter identifiable patient data.</strong> Conversations are stored and audited under our UK GDPR data processing policy. Delete your data at any time in Settings.</p>
            </div>
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>AI responses may contain errors.</strong> Always cross-reference with current CSP, NICE, and ACPR guidance. This tool does not replace specialist clinical expertise or individual patient assessment.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p><strong>Usage limit:</strong> 30 messages per hour to ensure fair access for all users.</p>
            </div>
          </div>
        )}

        {variant === "ot" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>OT clinical decision support, not a substitute for assessment.</strong> This AI assistant is grounded in the RCOT Code of Ethics and Professional Conduct, NICE guidelines (NG100, NG206, NG65, NG226), the COT Fatigue Management Programme, and EULAR recommendations for physical activity in rheumatic diseases. It does not replace clinical assessment or your professional judgment.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>Your HCPC accountability is unaffected.</strong> All OT assessments, splinting decisions, joint protection advice, and ADL recommendations remain your professional responsibility. Do not apply general guidance directly to individual patients without a full occupational therapy assessment.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>Do not enter identifiable patient data.</strong> Conversations are stored and audited under our UK GDPR data processing policy. Never input patient names, NHS numbers, or other direct identifiers. Delete your data at any time in Settings.</p>
            </div>
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>AI responses may contain errors.</strong> Always cross-reference with current RCOT, NICE, and COT guidance before applying to clinical practice. This tool does not replace specialist OT expertise or formal outcome measure training (COPM, DASH, HAQ-DI).</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p><strong>Usage limit:</strong> 30 messages per hour to ensure fair access for all users.</p>
            </div>
          </div>
        )}

        {variant === "patient" && (
          <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>General information only.</strong> This AI guide cannot diagnose conditions, prescribe medication, or provide personal medical advice. Always speak to your healthcare team about your own care.</p>
            </div>
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p><strong>In an emergency, call 999.</strong> For urgent medical concerns, call NHS 111. Do not rely on this AI in a medical emergency.</p>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
              <p><strong>Your conversations are stored privately</strong> and used only to maintain your chat history. You can delete all your data at any time in Settings.</p>
            </div>
          </div>
        )}

        <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 leading-relaxed">
          By continuing you confirm you have read and understood the above. Your data is processed under our{" "}
          <a href="/privacy" className="underline text-slate-700" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}
          in accordance with UK GDPR / DPA 2018.
        </div>

        <Button
          onClick={handleAccept}
          className={`w-full ${btnClass} text-white rounded-xl h-12`}
        >
          I understand and agree — continue
        </Button>

        <p className="text-center text-xs text-slate-400 mt-2">
          This notice will only appear once on this device.{" "}
          <a href="/privacy" className="underline hover:text-slate-600">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
